import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from './ui/button';
import { usePublish } from '../contexts/PublishContext';

const STEPS = [
  {
    id: 'show-details',
    label: 'Show Details',
    path: (id?: string) => (id ? `/publish/${id}` : '/publish'),
  },
  {
    id: 'internal-details',
    label: 'Internal Details',
    path: (id?: string) => `/publish/${id}/internal-details`,
  },
  {
    id: 'thumbnails',
    label: 'Thumbnails',
    path: (id?: string) => `/publish/${id}/thumbnails`,
  },
  {
    id: 'content',
    label: 'Content',
    path: (id?: string) => `/publish/${id}/content`,
  },
  {
    id: 'review',
    label: 'Review & Publish',
    path: (id?: string) => `/publish/${id}/preview`,
  },
];

function getActiveStepIndex(pathname: string, showId?: string): number {
  if (!showId || pathname === `/publish/${showId}` || pathname === '/publish') return 0;
  if (pathname.includes('/internal-details')) return 1;
  if (pathname.includes('/thumbnails')) return 2;
  if (pathname.includes('/content')) return 3;
  if (pathname.includes('/preview')) return 4;
  return 0;
}

interface PublishLayoutProps {
  children: React.ReactNode;
  onSave?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  showContinue?: boolean;
}

export function PublishLayout({
  children,
  onSave,
  onContinue,
  continueLabel = 'Continue',
  showContinue = true,
}: PublishLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { showId } = useParams<{ showId: string }>();
  const { state } = usePublish();

  const activeStep = getActiveStepIndex(location.pathname, showId);
  const showTitle = state?.details?.title || 'Untitled Show';

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="h-[49px] border-b flex items-center justify-between px-4 bg-background shrink-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 text-muted-foreground"
            onClick={() => navigate('/all-shows')}
          >
            ← All Shows
          </Button>
          <span className="text-muted-foreground/40 shrink-0">|</span>
          <span className="text-sm font-medium truncate">{showTitle}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onSave && (
            <Button variant="outline" size="sm" onClick={onSave}>
              Save Draft
            </Button>
          )}
          {showContinue && onContinue && (
            <Button variant="default" size="sm" onClick={onContinue}>
              {continueLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left stepper sidebar */}
        <aside className="w-52 border-r shrink-0 flex flex-col py-8 px-5 overflow-y-auto bg-background">
          {STEPS.map((step, index) => {
            const isDone = index < activeStep;
            const isActive = index === activeStep;
            const isClickable = showId || index === 0;

            return (
              <div key={step.id} className="flex flex-col">
                <button
                  className={`flex items-center gap-3 text-left py-0.5 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                  onClick={() => {
                    if (!isClickable) return;
                    navigate(step.path(showId));
                  }}
                  disabled={!isClickable}
                >
                  {/* Circle indicator */}
                  <div
                    className={[
                      'w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold border-2 transition-colors',
                      isDone ? 'bg-primary border-primary text-primary-foreground' : '',
                      isActive ? 'border-primary text-primary bg-primary/5' : '',
                      !isDone && !isActive ? 'border-border text-muted-foreground/40' : '',
                    ].join(' ')}
                  >
                    {isDone ? <Check className="w-3 h-3" /> : <span>{index + 1}</span>}
                  </div>

                  {/* Label */}
                  <span
                    className={[
                      'text-sm transition-colors leading-tight',
                      isActive ? 'text-foreground font-medium' : '',
                      isDone ? 'text-foreground/80' : '',
                      !isDone && !isActive ? 'text-muted-foreground/40' : '',
                    ].join(' ')}
                  >
                    {step.label}
                  </span>
                </button>

                {/* Vertical connector */}
                {index < STEPS.length - 1 && (
                  <div
                    className={`ml-[11px] w-px my-1.5 h-5 ${isDone ? 'bg-primary/50' : 'bg-border'}`}
                  />
                )}
              </div>
            );
          })}
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
